import { useCallback, useEffect } from 'react';

import { useAuthentication } from '@givebacks/api';
import { User } from '@givebacks/api/core';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { LogoGivebacksBlue } from 'ui/Icons';

import OAuth from '../../../components/auth/OAuth';
import { AuthContentForm } from '../../../components/organisms/forms/AuthContentForm';
import { separateQueryParamsFromRedirectUrl } from './utils';

const AuthLayout = () => {
  const isSignIn = useMatch('/sign-in');
  const isOAuth = useMatch('/oauth/:provider');
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, { open }] = useDisclosure(false);
  const { isAuthenticated, logIn } = useAuthentication();

  const navigate = useNavigate();

  /** Switches between sign in and sign up retaining the search params */
  const handleSwitchForm = useCallback(
    (isSignIn: boolean) => {
      navigate(`/${isSignIn ? 'sign-in' : 'sign-up'}?${searchParams.toString()}`);
    },
    [navigate, searchParams]
  );

  /**
   * On Sign In Success, we need to redirect to the page that the user was trying to access.
   * If no redirect is provided, we redirect to the home page.
   */
  const onSuccess = useCallback(
    async (user: User) => {
      logIn(user);
      const redirect = searchParams.get('redirect');

      // IF the logged in user is marked as a first time login- we'll want them to go to the welcome screen
      if (user.first_givebacks_login || !user.completed_supporter_onboarding) {
        return navigate('/onboarding/welcome');
      }

      if (redirect) {
        const { redirectUrl, queryParams } = separateQueryParamsFromRedirectUrl(redirect);
        queryParams.forEach((value, key) => {
          searchParams.set(key, value);
        });
        searchParams.delete('redirect');
        // Make sure to remove the redirect param from the search params so we don't forward it to the redirect url
        setSearchParams(searchParams);
        return navigate(`${redirectUrl}?${searchParams.toString()}`);
      } else {
        return navigate(`/?${searchParams.toString()}`);
      }
    },
    [navigate, searchParams, logIn, setSearchParams]
  );

  // If the user is authenticated, we redirect to the home page. Otherwise, we open the modal.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    } else {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only set to open on mount to ensure there is a transition
  }, []);

  return (
    <Modal
      opened={isOpen}
      onClose={() => {}}
      withCloseButton={false}
      title={<LogoGivebacksBlue w={117} />}
    >
      <AuthContentForm
        showSignIn={Boolean(isSignIn)}
        onFormSwitch={handleSwitchForm}
        onSuccess={onSuccess}
      />
      {/* Oauth gets optionally rendered in the modal, but we want to ensure it's rendered in the root with access to the parent functionality instead of treating it as a child route */}
      {isOAuth ? <OAuth onSuccess={onSuccess} /> : null}
    </Modal>
  );
};

export { AuthLayout };
